import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Aviso,
  Botao,
  Campo,
  CampoLongo,
  Cartao,
  Chip,
  GrupoDeOpcoes,
  Opcao,
} from "./ui";

const CORES = [
  "--accent-on-dark",
  "--accent-on-light",
  "--accent-line",
  "--ink-900",
  "--ink-800",
  "--ink-700",
  "--ink-600",
  "--glow-petroleo",
  "--glow-transicao",
  "--paper-100",
  "--paper-200",
  "--card",
  "--line-light",
  "--text-strong",
  "--text-body",
  "--text-muted",
  "--on-dark",
  "--on-dark-body",
  "--on-dark-muted",
  "--good-text",
  "--good-superficie",
  "--warn-text",
  "--warn-superficie",
  "--critical-text",
  "--critical-superficie",
] as const;

/* O motor de formulário da F5 vai validar com este mesmo par (react-hook-form + zod). A
   amostra existe para provar que a combinação está de pé e que o estado de erro do `Campo`
   aparece de verdade — não é uma tela de cadastro. */
const esquemaDaAmostra = z.object({
  // A mensagem de tipo também em português: sem ela, um valor ausente (e não vazio) sai
  // com o texto padrão do zod, em inglês, na cara de quem preenche.
  email: z
    .string({ error: "Informe o e-mail." })
    .min(1, "Informe o e-mail.")
    .email("E-mail inválido — o formato esperado é nome@empresa.com.br."),
  perfil: z.enum(["admin_cliente", "key_user", "ti"], {
    error: "Escolha um perfil.",
  }),
  observacao: z.string().max(280, "No máximo 280 caracteres.").optional(),
});

type DadosDaAmostra = z.infer<typeof esquemaDaAmostra>;

const PERFIS: ReadonlyArray<{ valor: DadosDaAmostra["perfil"]; titulo: string; descricao: string }> =
  [
    {
      valor: "admin_cliente",
      titulo: "Administrador do cliente",
      descricao: "gerencia os usuários da própria organização",
    },
    {
      valor: "key_user",
      titulo: "Key user",
      descricao: "responde a trilha de kickoff de um módulo",
    },
    { valor: "ti", titulo: "TI do cliente", descricao: "responde a trilha de infraestrutura" },
  ];

export function AmostraDoDesignSystem() {
  const [validado, setValidado] = useState<DadosDaAmostra | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
  } = useForm<DadosDaAmostra>({
    resolver: zodResolver(esquemaDaAmostra),
    mode: "onSubmit",
  });

  return (
    <div className="grid gap-6">
      <Aviso tom="marca">
        Estes são os componentes e tokens do padrão visual GEOvendas aplicado ao portal.
      </Aviso>

      <Cartao titulo="Paleta">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 lg:grid-cols-8 mt-2">
          {CORES.map((cor) => (
            <div className="amostra-cor" key={cor}>
              <div className="amostra-cor__area" style={{ background: `var(${cor})` }} />
              <div className="amostra-cor__nome" title={cor}>{cor}</div>
            </div>
          ))}
        </div>
        <p className="nota-rodape mt-3">
          Os valores oficiais ficam em <code>:root</code>. O tema escuro adapta as superfícies
          com os mesmos tokens. O turquesa da marca fica reservado ao logo.
        </p>
      </Cartao>

      <Cartao titulo="Botões e chips">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Botao>Ação principal</Botao>
          <Botao variante="verde">Confirmar</Botao>
          <Botao variante="fantasma">Secundária</Botao>
          <Botao variante="fantasma" tamanho="pequeno">
            Pequena
          </Botao>
          <Botao disabled>Indisponível</Botao>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Chip estado="ok">Concluído</Chip>
          <Chip estado="aguardando">Em andamento</Chip>
          <Chip estado="atencao">Pendente</Chip>
          <Chip estado="erro">Atrasado</Chip>
          <Chip estado="neutro">Não iniciado</Chip>
        </div>
      </Cartao>

      <Cartao titulo="Campos, seleção e validação">
        <form
          className="grid gap-4 mt-2"
          onSubmit={(evento) => {
            void handleSubmit((dados) => setValidado(dados))(evento);
          }}
          noValidate
        >
          <Campo
            rotulo="E-mail de contato"
            ajuda="Envie um valor inválido para ver o estado de erro do campo."
            obrigatorio
            placeholder="nome@empresa.com.br"
            autoComplete="email"
            {...(errors.email?.message ? { erro: errors.email.message } : {})}
            {...register("email")}
          />

          <GrupoDeOpcoes rotulo="Perfil no portal">
            {PERFIS.map((perfil) => (
              <Opcao
                key={perfil.valor}
                tipo="radio"
                value={perfil.valor}
                titulo={perfil.titulo}
                descricao={perfil.descricao}
                {...(perfil.valor === "ti"
                  ? { etiqueta: "sensível", classeDaEtiqueta: "etiqueta--sensivel" }
                  : {})}
                {...register("perfil")}
              />
            ))}
          </GrupoDeOpcoes>
          {errors.perfil?.message ? (
            <p className="campo__erro" role="alert">
              {errors.perfil.message}
            </p>
          ) : null}

          <CampoLongo
            rotulo="Observação"
            ajuda="Opcional, até 280 caracteres."
            rows={3}
            {...(errors.observacao?.message ? { erro: errors.observacao.message } : {})}
            {...register("observacao")}
          />

          <div className="flex flex-wrap items-center gap-3">
            <Botao type="submit" variante="verde">
              Validar
            </Botao>
            {validado ? (
              <Chip estado="ok">
                Válido — {validado.email} · {validado.perfil}
              </Chip>
            ) : isSubmitted ? (
              <Chip estado="atencao">Corrija os campos marcados</Chip>
            ) : null}
          </div>
        </form>
      </Cartao>
    </div>
  );
}
